// API Service Layer for DynamoDB Operations

import { Product, Order, LoginRequest, RegisterRequest, AuthResponse, User, Category } from '../types';
import Cookies from 'js-cookie';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Generic API request function
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Don't set Content-Type if body is FormData (for file uploads)
  const isFormData = options.body instanceof FormData;
  
  const defaultHeaders: HeadersInit = isFormData ? {} : {
    'Content-Type': 'application/json',
  };
  
  const defaultOptions: RequestInit = {
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    ...options,
  };

  // Debug logging for auth issues
  if (options.headers && (options.headers as any).Authorization) {
    console.log('API Request with Auth:', {
      endpoint,
      method: options.method || 'GET',
      authHeader: (options.headers as any).Authorization.substring(0, 20) + '...'
    });
  }

  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Provide user-friendly messages for common HTTP errors
      let defaultMessage = `HTTP error! status: ${response.status}`;
      if (response.status === 401) {
        defaultMessage = endpoint.includes('/auth/login') 
          ? 'Invalid email or password. Please try again.'
          : 'Authentication required';
      } else if (response.status === 403) {
        defaultMessage = 'Access denied. You do not have permission to perform this action.';
      } else if (response.status === 404) {
        defaultMessage = 'The requested resource was not found.';
      } else if (response.status === 500) {
        defaultMessage = 'An error occurred on the server. Please try again later.';
      }
      
      throw new Error(errorData.message || errorData.error || defaultMessage);
    }
    
    // Handle empty responses (204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
}

// Product API
export const productApi = {
  // Get all products
  async getAll(): Promise<Product[]> {
    return apiRequest<Product[]>('/products');
  },

  // Get product by ID
  async getById(id: string): Promise<Product> {
    return apiRequest<Product>(`/products/${id}`);
  },

  // Create new product
  async create(product: Omit<Product, 'id'>): Promise<Product> {
    const token = Cookies.get('authToken');
    return apiRequest<Product>('/products', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(product),
    });
  },

  // Update product
  async update(id: string, product: Partial<Product>): Promise<Product> {
    const token = Cookies.get('authToken');
    return apiRequest<Product>(`/products/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(product),
    });
  },

  // Delete product
  async delete(id: string): Promise<void> {
    const token = Cookies.get('authToken');
    return apiRequest<void>(`/products/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Order API
export const orderApi = {
  // Get all orders
  async getAll(): Promise<Order[]> {
    const token = Cookies.get('authToken');
    return apiRequest<Order[]>('/orders', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get order by ID
  async getById(id: string): Promise<Order> {
    const token = Cookies.get('authToken');
    return apiRequest<Order>(`/orders/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get orders by status
  async getByStatus(status: Order['status']): Promise<Order[]> {
    const token = Cookies.get('authToken');
    return apiRequest<Order[]>(`/orders?status=${status}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get orders by customer email
  async getByCustomer(email: string): Promise<Order[]> {
    const token = Cookies.get('authToken');
    return apiRequest<Order[]>(`/orders?customer=${email}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Create new order
  async create(order: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>): Promise<Order> {
    return apiRequest<Order>('/orders', {
      method: 'POST',
      body: JSON.stringify(order),
    });
  },

  // Update order
  async update(id: string, updates: Partial<Order>): Promise<Order> {
    const token = Cookies.get('authToken');
    return apiRequest<Order>(`/orders/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });
  },

  // Update order status
  async updateStatus(id: string, status: Order['status'], notes?: string): Promise<Order> {
    const token = Cookies.get('authToken');
    return apiRequest<Order>(`/orders/${id}/status`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status, notes }),
    });
  },

  // Update internal status (admin only)
  async updateInternalStatus(id: string, internalStatus: Order['internalStatus'], internalNotes?: string): Promise<Order> {
    const token = Cookies.get('authToken');
    return apiRequest<Order>(`/orders/${id}/internal-status`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ internalStatus, internalNotes }),
    });
  },

  // Delete order
  async delete(id: string): Promise<void> {
    const token = Cookies.get('authToken');
    return apiRequest<void>(`/orders/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Settings API
export const settingsApi = {
  // Get all settings
  async getAll(): Promise<Record<string, any>> {
    return apiRequest<Record<string, any>>('/settings');
  },

  // Get pricing settings
  async getPricing(): Promise<any> {
    return apiRequest<any>('/settings/pricing');
  },

  // Update pricing settings
  async updatePricing(settings: any): Promise<any> {
    return apiRequest<any>('/settings/pricing', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },

  // Get general settings
  async getGeneral(): Promise<any> {
    return apiRequest<any>('/settings/general');
  },

  // Update general settings
  async updateGeneral(settings: any): Promise<any> {
    return apiRequest<any>('/settings/general', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },
};

// Users API (Admin only)
export const usersApi = {
  // Get all users
  async getAll(): Promise<User[]> {
    const token = Cookies.get('authToken');
    const users = await apiRequest<any[]>('/users', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    // Convert date strings to Date objects
    return users.map(user => ({
      ...user,
      createdAt: new Date(user.createdAt),
      updatedAt: new Date(user.updatedAt)
    }));
  },

  // Get user by ID
  async getById(id: string): Promise<User> {
    const token = Cookies.get('authToken');
    const user = await apiRequest<any>(`/users/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return {
      ...user,
      createdAt: new Date(user.createdAt),
      updatedAt: new Date(user.updatedAt)
    };
  },

  // Update user
  async update(id: string, updates: Partial<User>): Promise<User> {
    const token = Cookies.get('authToken');
    const user = await apiRequest<any>(`/users/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });
    return {
      ...user,
      createdAt: new Date(user.createdAt),
      updatedAt: new Date(user.updatedAt)
    };
  },

  // Update user role
  async updateRole(id: string, role: 'admin' | 'customer'): Promise<User> {
    const token = Cookies.get('authToken');
    const user = await apiRequest<any>(`/users/${id}/role`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ role }),
    });
    return {
      ...user,
      createdAt: new Date(user.createdAt),
      updatedAt: new Date(user.updatedAt)
    };
  },

  // Delete user
  async delete(id: string): Promise<void> {
    const token = Cookies.get('authToken');
    return apiRequest<void>(`/users/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Authentication API
export const authApi = {
  // Login
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return apiRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  // Register
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    return apiRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  // Validate token
  async validateToken(token: string): Promise<User> {
    return apiRequest<User>('/auth/validate', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get current user
  async getCurrentUser(): Promise<User> {
    const token = Cookies.get('authToken');
    return apiRequest<User>('/auth/me', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const token = Cookies.get('authToken');
    return apiRequest<{ message: string }>('/auth/change-password', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });
  },
};

// Category API
export const categoryApi = {
  // Get all categories
  async getAll(): Promise<Category[]> {
    return apiRequest<Category[]>('/categories');
  },

  // Get category by ID
  async getById(id: string): Promise<Category> {
    return apiRequest<Category>(`/categories/${id}`);
  },

  // Create new category
  async create(category: Omit<Category, 'id'>): Promise<Category> {
    const token = Cookies.get('authToken');
    console.log('Creating category with token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
    return apiRequest<Category>('/categories', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(category),
    });
  },

  // Update category
  async update(id: string, category: Partial<Category>): Promise<Category> {
    const token = Cookies.get('authToken');
    return apiRequest<Category>(`/categories/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(category),
    });
  },

  // Delete category
  async delete(id: string): Promise<void> {
    const token = Cookies.get('authToken');
    return apiRequest<void>(`/categories/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Payment API
export const paymentApi = {
  // Create checkout session
  async createCheckoutSession(data: {
    orderId: string;
    items: any[];
    subtotal: number;
    tax: number;
    total: number;
  }): Promise<{ sessionId: string; url: string }> {
    const token = Cookies.get('authToken');
    return apiRequest<{ sessionId: string; url: string }>('/payment/create-checkout-session', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
  },

  // Verify session
  async verifySession(sessionId: string): Promise<{
    status: string;
    orderId: string;
    customerEmail: string;
    shippingAddress: any;
  }> {
    const token = Cookies.get('authToken');
    return apiRequest('/payment/verify-session', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ sessionId }),
    });
  },

  // Process refund
  async refund(data: {
    orderId: string;
    amount: number;
    reason?: string;
  }): Promise<{ refund: any; order: Order }> {
    const token = Cookies.get('authToken');
    return apiRequest<{ refund: any; order: Order }>('/payment/refund', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
  },
};

// Health check API
export const healthApi = {
  async check(): Promise<{ status: string; timestamp: string }> {
    return apiRequest<{ status: string; timestamp: string }>('/health');
  },
};

// Export all APIs
export const api = {
  products: productApi,
  orders: orderApi,
  settings: settingsApi,
  users: usersApi,
  auth: authApi,
  categories: categoryApi,
  payment: paymentApi,
  health: healthApi,
};

export default api;